import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Receipt, IndianRupee, AlertCircle, UserSearch, Plus, Search, ArrowUp, ArrowDown, Check, X, ChevronRight, ChevronDown, Trash2, Download, Loader2, UserPlus, Lock, Edit3 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useListInvoicesQuery, useLazyListInvoicesQuery, useListDuesQuery, useUpdateInvoiceBillingMutation, useUpdateInvoicePaymentMethodMutation, useCreateManualBillingMutation, useInvoiceTotalsQuery, useSettleDuesMutation, useGetInvoiceQuery, useAddPaymentMutation, usePeekNextInvoiceNumberQuery, useLazyFindInvoiceByNumberQuery, useDeleteInvoiceMutation, useLazyDownloadInvoiceQuery } from '../../api/billingApi';
import { selectHasRole, selectIsReceptionist } from '../auth/authSlice';
import { useListProcedureCatalogQuery } from '../../api/procedureCatalogApi';
import HomeoToggle, { useExcludeHomeopathy } from '../../components/HomeoToggle';
import { rangeForPreset } from '../../components/DateRangePicker';
import { useGetPatientOverviewQuery } from '../../api/patientsApi';
import { useCreatePatientMutation } from '../../api/patientsApi';
import { useInfiniteQuery } from '../../hooks/useInfiniteQuery';
import { fetchAllPages } from '../../utils/fetchAllPages';
import ExportButtons from '../../components/ExportButtons';
import PatientCombobox from '../../components/PatientCombobox';
import CollectPaymentModal from '../appointments/CollectPaymentModal';
import EditBillModal from './EditBillModal';
import ReceiptsTab from './ReceiptsTab';

const PAYMENT_METHODS = [
  { value: 'Cash', label: 'Cash' }, { value: 'Card', label: 'Card' }, { value: 'Upi', label: 'UPI' },
  { value: 'BankTransfer', label: 'Bank Transfer' }, { value: 'Online', label: 'Online' },
];

const fmt = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v ?? 0);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—');
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

function Kpi({ label, value, accent }) {
  return (
    <div className="kpi-card" style={{ '--kpi-accent': accent || 'var(--blue-500)' }}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}

export default function BillingPage() {
  // The Overview section was removed (2026-07) — the register is the default landing tab.
  const [tab, setTab] = useState('invoices');
  // Shared with BillingRegisterTab's own useSearchParams call (same underlying URL state) — used
  // so a receipt row's "open invoice" link can jump straight to the Invoices tab's deep-link modal.
  const [, setSearchParams] = useSearchParams();
  const openInvoice = (invoiceId) => {
    if (!invoiceId) return;
    setTab('invoices');
    setSearchParams({ openId: invoiceId });
  };

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb"><span>Home</span><span className="bc-sep">/</span><span className="bc-cur">Billing</span></div>
        <div className="page-header-row">
          <div>
            <h1>Billing</h1>
            <div className="page-header-sub">Per-patient billing, invoices and dues.</div>
          </div>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab-btn ${tab === 'invoices' ? 'active' : ''}`} onClick={() => setTab('invoices')}>
          <Receipt size={14} /> Billing register
        </button>
        <button className={`tab-btn ${tab === 'receipts' ? 'active' : ''}`} onClick={() => setTab('receipts')}>
          <Receipt size={14} /> Receipts
        </button>
        <button className={`tab-btn ${tab === 'patient' ? 'active' : ''}`} onClick={() => setTab('patient')}>
          <UserSearch size={14} /> Patient billing
        </button>
        <button className={`tab-btn ${tab === 'dues' ? 'active' : ''}`} onClick={() => setTab('dues')}>
          <AlertCircle size={14} /> Outstanding dues
        </button>
      </div>

      {tab === 'patient' && <PatientBillingTab />}
      {tab === 'invoices' && <BillingRegisterTab />}
      {tab === 'receipts' && <ReceiptsTab onOpenInvoice={openInvoice} />}
      {tab === 'dues' && <DuesTab />}
    </>
  );
}

function PatientBillingTab() {
  const [patient, setPatient] = useState(null);
  const [showCollect, setShowCollect] = useState(false);
  const { data: o, isFetching } = useGetPatientOverviewQuery(patient?.id, { skip: !patient?.id });

  return (
    <>
      <div className="toolbar" style={{ alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, maxWidth: 460 }}>
          <PatientCombobox value={patient} onChange={setPatient} placeholder="Search a patient by name, phone or ID…" />
        </div>
        {patient && (
          <button className="btn btn-primary" onClick={() => setShowCollect(true)}>
            <Plus size={16} /> New invoice / payment
          </button>
        )}
      </div>

      {!patient && (
        <div className="empty-state">
          <UserSearch className="es-icon" size={42} />
          <h3>Select a patient</h3>
          <p>Pick a patient to see their billing summary, invoices and payment history.</p>
        </div>
      )}

      {patient && o && (
        <>
          <div className="kpi-grid" style={{ marginBottom: 16 }}>
            <Kpi label="Total paid" value={fmt(o.totalPaid)} accent="var(--green-500)" />
            <Kpi label="Outstanding" value={fmt(o.outstanding)} accent="var(--amber-500)" />
            <Kpi label="Overdue" value={fmt(o.overdueAmount)} accent="var(--red-500)" />
            <Kpi label="Advance balance" value={fmt(o.advanceBalance)} accent="var(--blue-700)" />
            <Kpi label="Sessions by advance" value={o.sessionsCoveredByAdvance} />
            <Kpi label="Total billed" value={fmt(o.totalBilled)} />
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header"><h3>Invoices</h3></div>
            <InvoiceTable rows={o.invoices ?? []} loading={isFetching} />
          </div>

          <div className="card">
            <div className="card-header"><h3>Payment history</h3></div>
            <div className="tbl-wrap">
              <table>
                <thead><tr><th>Date</th><th>Amount</th><th>Method</th><th>Reference</th></tr></thead>
                <tbody>
                  {(o.payments ?? []).map((pmt) => (
                    <tr key={pmt.id}>
                      <td className="td-muted">{fmtDate(pmt.paidAt)}</td>
                      <td className="td-bold">{fmt(pmt.amount)}</td>
                      <td>{pmt.method}</td>
                      <td className="td-muted">{pmt.transactionReference || '—'}</td>
                    </tr>
                  ))}
                  {(o.payments ?? []).length === 0 && (
                    <tr><td colSpan={4} className="td-muted" style={{ textAlign: 'center', padding: 18 }}>No payments yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showCollect && patient && (
        <CollectPaymentModal patient={patient} onClose={() => setShowCollect(false)} />
      )}
    </>
  );
}

// ── Editable billing register ────────────────────────────────────────────────
const SORT_COLS = {
  invoiceNumber: 'Invoice #', issuedAt: 'Issued', patientCode: 'Patient ID', patientName: 'Patient',
  costPerSession: 'Cost / session', totalSessions: 'Sessions', totalCost: 'Total cost',
  discount: 'Discount', invoiced: 'Invoiced', amountPaid: 'Paid', balance: 'Balance',
};

const REGISTER_EXPORT_COLUMNS = [
  { key: 'invoiceNumber', label: 'Invoice #' },
  { key: 'issuedAt', label: 'Issued' },
  { key: 'patientCode', label: 'Patient ID' },
  { key: 'patientName', label: 'Patient' },
  { key: 'treatment', label: 'Treatment / procedure' },
  { key: 'costPerSession', label: 'Cost/session' },
  { key: 'totalSessions', label: 'Sessions' },
  { key: 'totalCost', label: 'Total cost' },
  { key: 'discount', label: 'Discount' },
  { key: 'invoiced', label: 'Invoiced' },
  { key: 'amountPaid', label: 'Paid' },
  { key: 'paymentMethods', label: 'Payment method' },
  { key: 'balance', label: 'Balance' },
  { key: 'status', label: 'Status' },
];

const REGISTER_COLUMNS = [
  { key: 'invoiceNumber', label: 'Invoice #', sortable: true },
  { key: 'issuedAt', label: 'Issued', sortable: true },
  { key: 'patientCode', label: 'Patient ID', sortable: true },
  { key: 'patientName', label: 'Patient', sortable: true },
  { key: 'treatmentSummary', label: 'Treatment / procedure' },
  { key: 'costPerSession', label: 'Cost / session', sortable: true, right: true },
  { key: 'totalSessions', label: 'Total sessions', sortable: true, right: true },
  { key: 'totalCost', label: 'Total cost', sortable: true, right: true },
  { key: 'discount', label: 'Discount', sortable: true, right: true },
  { key: 'invoiced', label: 'Invoiced (after disc.)', sortable: true, right: true },
  { key: 'amountPaid', label: 'Amount paid', sortable: true, right: true },
  { key: 'paymentMethods', label: 'Payment method' },
  { key: 'balance', label: 'Balance', sortable: true, right: true },
];

function BillingRegisterTab() {
  const [columnVisibility, setColumnVisibility] = useState(() => (
    REGISTER_COLUMNS.reduce((acc, col) => {
      acc[col.key] = true;
      return acc;
    }, {})
  ));
  const [balance, setBalance] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sortBy, setSortBy] = useState('issuedAt');
  const [sortDesc, setSortDesc] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showAddReceipt, setShowAddReceipt] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Global Homeopathy-omission toggle (persisted) feeds the backend filter.
  const [excludeHomeo] = useExcludeHomeopathy();

  // Non-page filter for the infinite hook — any change resets scroll to page 1.
  const filter = useMemo(
    () => ({ balance, search, from, to, sortBy, sortDesc, ...(excludeHomeo ? { excludeHomeopathy: true } : {}) }),
    [balance, search, from, to, sortBy, sortDesc, excludeHomeo],
  );
  const { items: rows, total, hasMore, isFetching, sentinelRef, patchItem, reset } =
    useInfiniteQuery(useListInvoicesQuery, filter, { pageSize: 25 });

  // Reconciliation totals over the SAME filter set (recomputed server-side on every change).
  const { data: totals } = useInvoiceTotalsQuery(filter);

  const [deleteInvoice] = useDeleteInvoiceMutation();
  const isAdmin = useSelector(selectHasRole('Admin'));
  const deleteBill = async (inv) => {
    if (!window.confirm(`Delete bill ${inv.invoiceNumber || ''}? This is IRREVERSIBLE — it reverses any unconsumed sessions it funded, and its invoice number becomes free for a future bill to reuse.`)) return;
    try {
      await deleteInvoice(inv.id).unwrap();
      toast.success(`Bill ${inv.invoiceNumber || ''} deleted`);
    } catch (e) {
      toast.error(e?.data?.error?.message || 'Could not delete this bill.');
    }
  };
  // ── Download Invoice (per-row) ──────────────────────────────────────────────
  // Tracks the single row currently compiling a PDF so we can show an inline spinner and block
  // double-clicks. The backend streams application/pdf; we turn it into a Blob, force a browser
  // download via a transient object URL, then revoke it to avoid leaking memory.
  const [triggerDownload] = useLazyDownloadInvoiceQuery();
  const [loadingInvoiceId, setLoadingInvoiceId] = useState(null);
  const handleDownloadInvoice = useCallback(async (inv) => {
    if (loadingInvoiceId) return; // one at a time — prevents click-spam
    setLoadingInvoiceId(inv.id);
    let url;
    try {
      const blob = await triggerDownload(inv.id).unwrap();
      url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${String(inv.invoiceNumber || 'INV').replace(/[^a-zA-Z0-9]+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      const status = err?.status;
      toast.error(
        status === 403 ? 'You do not have permission to download this invoice.'
          : status === 404 ? 'Invoice not found — it may have been deleted.'
            : 'Could not generate the invoice PDF. Please try again.'
      );
    } finally {
      if (url) URL.revokeObjectURL(url); // free the blob URL immediately
      setLoadingInvoiceId(null);
    }
  }, [loadingInvoiceId, triggerDownload]);

  const isReceptionist = useSelector(selectIsReceptionist);

  // Export the full filtered register (all pages), not just rows scrolled into view.
  const [triggerInvoices] = useLazyListInvoicesQuery();
  const getExportRows = async () => {
    const all = await fetchAllPages(triggerInvoices, filter, 200);
    return all.map((i) => {
      const totalCost = num(i.costPerSession) * num(i.totalSessions);
      const invoiced = i.totalAmount ?? 0;
      const balance = (i.amountPaid ?? 0) - invoiced; // Paid − Total (dues negative, advance positive)
      return {
        invoiceNumber: i.invoiceNumber || '',
        issuedAt: fmtDate(i.issuedAt),
        patientCode: i.patientCode || '',
        patientName: i.patientName || '',
        treatment: i.treatmentSummary || '',
        costPerSession: num(i.costPerSession),
        totalSessions: i.totalSessions || 0,
        totalCost,
        discount: num(i.discountAmount),
        invoiced,
        amountPaid: num(i.amountPaid),
        paymentMethods: i.paymentMethods || '',
        balance,
        status: i.status || '',
      };
    });
  };

  // Row-click drill-down for the receipts drawer (one expanded at a time).
  const [expandedId, setExpandedId] = useState(null);

  // Deep link of the form /billing?openId=<guid> — e.g. clicking an invoice from global search.
  // Shown in its own modal (fetched directly by id) rather than trying to scroll/filter the
  // register down to reveal the row, since the target invoice may not be on the currently
  // loaded page of whatever filter happens to be active.
  const [searchParams, setSearchParams] = useSearchParams();
  const [deepLinkInvoiceId, setDeepLinkInvoiceId] = useState(null);
  useEffect(() => {
    const id = searchParams.get('openId');
    if (id) setDeepLinkInvoiceId(id);
  }, [searchParams]);
  const closeDeepLinkInvoice = () => {
    setDeepLinkInvoiceId(null);
    if (searchParams.get('openId')) {
      const next = new URLSearchParams(searchParams);
      next.delete('openId');
      setSearchParams(next, { replace: true });
    }
  };

  // Centralized edit: ONE dialog per bill (no inline cell editing). The bill being edited:
  const [editTarget, setEditTarget] = useState(null);

  const setSort = (col) => {
    if (sortBy === col) setSortDesc((d) => !d);
    else { setSortBy(col); setSortDesc(true); }
  };

  // Render helpers as FUNCTION CALLS (not nested components) so the same <th>/<input>
  // element type is reconciled in place across re-renders — inputs keep focus while typing.
  const sortTh = (col, children, right = false) => (
    <th
      className="bill-sort-th"
      style={{ cursor: SORT_COLS[col] ? 'pointer' : 'default', textAlign: right ? 'right' : 'left', whiteSpace: 'nowrap' }}
      onClick={() => SORT_COLS[col] && setSort(col)}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, justifyContent: right ? 'flex-end' : 'flex-start' }}>
        {children}
        {sortBy === col && (sortDesc ? <ArrowDown size={12} /> : <ArrowUp size={12} />)}
      </span>
    </th>
  );

  const visibleColumns = REGISTER_COLUMNS.filter((col) => columnVisibility[col.key]);
  // +1 for the leading chevron, +1 for the trailing edit-actions column.
  const visibleCount = visibleColumns.length + 2;

  // Display-only cells — ALL edits go through the single Edit Bill dialog now.
  const renderCell = (key, inv, totals) => {
    switch (key) {
      case 'invoiceNumber':
        return <td className="td-mono td-bold">{inv.invoiceNumber || '—'}</td>;
      case 'issuedAt':
        return <td className="td-muted">{fmtDate(inv.issuedAt)}</td>;
      case 'patientCode':
        return <td className="td-mono">{inv.patientCode || '—'}</td>;
      case 'patientName':
        return <td>{inv.patientName || inv.patientId?.slice(0, 8)}</td>;
      case 'treatmentSummary':
        return (
          <td className="bill-treatment" title={inv.treatmentSummary || ''}>
            <span className="td-muted">{inv.treatmentSummary || '—'}</span>
          </td>
        );
      case 'costPerSession':
        return <td style={{ textAlign: 'right' }}>{fmt(inv.costPerSession)}</td>;
      case 'totalSessions':
        return <td style={{ textAlign: 'right' }}>{inv.totalSessions || 0}</td>;
      case 'totalCost':
        return <td style={{ textAlign: 'right' }} className="td-bold">{fmt(totals.totalCost)}</td>;
      case 'discount':
        return <td style={{ textAlign: 'right' }}>{fmt(inv.discountAmount)}</td>;
      case 'invoiced':
        return <td style={{ textAlign: 'right' }} className="td-bold">{fmt(totals.invoiced)}</td>;
      case 'amountPaid':
        return <td style={{ textAlign: 'right' }}>{fmt(inv.amountPaid)}</td>;
      case 'paymentMethods': {
        const methodList = (inv.paymentMethods || '').split(',').map((m) => m.trim()).filter(Boolean);
        return (
          <td className="td-muted" style={{ whiteSpace: 'nowrap' }}>
            {methodList.length > 0
              ? methodList.map((m) => <span key={m} className="badge b-gray" style={{ marginRight: 4 }}>{m}</span>)
              : '—'}
          </td>
        );
      }
      case 'balance':
        return (
          <td style={{ textAlign: 'right', color: totals.balColor }} className="td-bold">
            {totals.balance > 0 ? `+${fmt(totals.balance)}` : fmt(totals.balance)}
          </td>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 240, maxWidth: 380 }}>
          <Search size={15} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-muted)' }} />
          <input
            className="form-control" style={{ paddingLeft: 32 }}
            placeholder="Search patient, ID, phone or invoice #…"
            value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <select className="form-control" style={{ width: 180 }} value={balance}
          onChange={(e) => setBalance(e.target.value)}>
          <option value="">All Patient</option>
          <option value="new">New Patient</option>
          <option value="renewed">Renewed</option>
          <option value="outstanding">Outstanding</option>
          <option value="nodues">No Dues</option>
          <option value="advance">Advance</option>
        </select>
        <input className="form-control" style={{ width: 150 }} type="date" title="From issue date"
          value={from ? from.slice(0, 10) : ''}
          onChange={(e) => setFrom(e.target.value ? new Date(e.target.value).toISOString() : '')} />
        <input className="form-control" style={{ width: 150 }} type="date" title="To issue date"
          value={to ? to.slice(0, 10) : ''}
          onChange={(e) => setTo(e.target.value ? new Date(e.target.value).toISOString() : '')} />
        <button
          className="btn btn-ghost btn-sm"
          title="Current Indian financial year (1 April → today)"
          onClick={() => {
            const r = rangeForPreset('fy');
            setFrom(r.from || '');
            setTo(r.to || '');
          }}
        >
          Current FY
        </button>
        <button className="btn btn-outline btn-sm" onClick={() => setShowAddReceipt(true)}>
          <Receipt size={15} /> Add New Receipt
        </button>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
          <Plus size={15} /> Add Billing
        </button>
        <HomeoToggle />
        <ExportButtons
          filename="billing-register"
          title="Billing Register"
          subtitle={`${total?.toLocaleString() ?? 0} invoices · exported ${new Date().toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}`}
          columns={REGISTER_EXPORT_COLUMNS}
          getRows={getExportRows}
          getSummary={(rows) => [
            { label: 'Total invoices', value: rows.length },
            { label: 'Unique patients', value: new Set(rows.map((r) => r.patientCode)).size },
            { label: 'Gross billed', value: fmt(rows.reduce((s, r) => s + num(r.invoiced), 0)) },
            { label: 'Net collected', value: fmt(rows.reduce((s, r) => s + num(r.amountPaid), 0)) },
            { label: 'Outstanding dues', value: fmt(rows.reduce((s, r) => s + (r.balance < 0 ? -r.balance : 0), 0)) },
            { label: 'Advance credits', value: fmt(rows.reduce((s, r) => s + (r.balance > 0 ? r.balance : 0), 0)) },
          ]}
          orientation="landscape"
        />
        <details className="col-toggle">
          <summary className="btn btn-ghost btn-sm">Columns</summary>
          <div className="col-toggle-panel">
            {REGISTER_COLUMNS.map((col) => (
              <label key={col.key} className="col-toggle-item">
                <input
                  type="checkbox"
                  checked={columnVisibility[col.key]}
                  onChange={() => setColumnVisibility((v) => ({ ...v, [col.key]: !v[col.key] }))}
                />
                <span>{col.label}</span>
              </label>
            ))}
            <div className="col-toggle-actions">
              <button
                className="btn btn-ghost btn-sm"
                type="button"
                onClick={() => setColumnVisibility(REGISTER_COLUMNS.reduce((acc, col) => { acc[col.key] = true; return acc; }, {}))}
              >
                Reset
              </button>
            </div>
          </div>
        </details>
        {(balance || from || to || search) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setSearchInput(''); setSearch(''); setBalance(''); setFrom(''); setTo(''); }}>
            Clear
          </button>
        )}
      </div>

      <div className="tbl-wrap tbl-elevated">
        <table className="bill-register">
          <thead>
            <tr>
              <th style={{ width: 30 }}></th>{/* chevron column for the receipts drawer */}
              {visibleColumns.map((col) => {
                if (col.sortable) return <React.Fragment key={col.key}>{sortTh(col.key, col.label, col.right)}</React.Fragment>;
                return (
                  <th key={col.key} style={{ textAlign: col.right ? 'right' : 'left', whiteSpace: 'nowrap' }}>
                    {col.label}
                  </th>
                );
              })}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isFetching && rows.length === 0 && (
              <tr><td colSpan={visibleCount} style={{ textAlign: 'center', padding: 24 }}>
                <div className="skeleton" style={{ height: 20, maxWidth: 320, margin: '0 auto' }} />
              </td></tr>
            )}
            {rows.map((inv) => {
              const totalCost = num(inv.costPerSession) * num(inv.totalSessions);
              const invoiced = Math.max(0, totalCost - Math.min(num(inv.discountAmount), totalCost));
              // Balance = Paid − Invoiced: dues are NEGATIVE (Outstanding), credit POSITIVE (Advance), 0 = No Dues.
              const balance = num(inv.amountPaid) - invoiced;
              const balColor = balance < 0 ? 'var(--red-700)' : (balance > 0 ? 'var(--blue-700)' : 'var(--green-700)');
              const totals = { totalCost, invoiced, balance, balColor };
              const isExpanded = expandedId === inv.id;
              return (
                <React.Fragment key={inv.id}>
                  <tr>
                    <td
                      className="td-muted"
                      style={{ cursor: 'pointer', textAlign: 'center' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedId((cur) => (cur === inv.id ? null : inv.id));
                      }}
                      title={isExpanded ? 'Hide receipts' : 'Show receipts'}
                    >
                      {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </td>
                    {visibleColumns.map((col) => (
                      <React.Fragment key={col.key}>
                        {renderCell(col.key, inv, totals)}
                      </React.Fragment>
                    ))}
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          title="Download Invoice"
                          aria-label={`Download invoice ${inv.invoiceNumber || ''}`}
                          disabled={loadingInvoiceId === inv.id}
                          onClick={(e) => { e.stopPropagation(); handleDownloadInvoice(inv); }}
                        >
                          {loadingInvoiceId === inv.id
                            ? <Loader2 size={14} className="spin" />
                            : <Download size={14} />}
                        </button>
                        {isReceptionist ? (
                          <span
                            title="Permission Denied: Cannot edit or delete bills"
                            style={{ display: 'inline-flex', alignItems: 'center', padding: '5px', color: 'var(--text-muted)', cursor: 'not-allowed' }}
                          >
                            <Lock size={13} />
                          </span>
                        ) : (
                          <>
                            <button
                              className="btn btn-outline btn-sm"
                              title={`Edit bill ${inv.invoiceNumber || ''}`}
                              onClick={(e) => { e.stopPropagation(); setEditTarget(inv); }}
                            >
                              <Edit3 size={13} /> Edit
                            </button>
                            {isAdmin && (
                              <button
                                className="btn btn-ghost btn-icon btn-sm"
                                title="Delete bill"
                                onClick={(e) => { e.stopPropagation(); deleteBill(inv); }}
                              >
                                <Trash2 size={14} color="var(--red-500)" />
                              </button>
                            )}
                          </>
                        )}
                      </span>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bill-drawer-row">
                      <td colSpan={visibleCount} style={{ padding: 0, background: 'var(--surface-alt, #fafafa)' }}>
                        <InvoiceReceiptsDrawer invoiceId={inv.id} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {!isFetching && rows.length === 0 && (
              <tr><td colSpan={visibleCount}>
                <div className="empty-state">
                  <IndianRupee className="es-icon" size={42} />
                  <h3>No invoices match</h3>
                  <p>Adjust the search or filters above.</p>
                </div>
              </td></tr>
            )}
          </tbody>
        </table>
        {/* Infinite-scroll sentinel — loads the next page of invoices as it enters view. */}
        {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
      </div>

      {/* TOTALING & SUMMARY — recalculated server-side over the active filter set */}
      {totals && (
        <div className="card card-body" style={{ marginTop: 12, display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center', fontSize: '0.84rem', fontWeight: 600 }}>
          <span style={{ textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.06em', color: 'var(--text-muted)' }}>Totaling &amp; Summary</span>
          <span>Invoices: <strong>{totals.invoiceCount.toLocaleString()}</strong></span>
          <span>Patients: <strong>{totals.patientCount.toLocaleString()}</strong></span>
          <span>Gross billed: <strong>{fmt(totals.grossBilled)}</strong></span>
          <span style={{ color: 'var(--green-700)' }}>Net collected: <strong>{fmt(totals.netCollected)}</strong></span>
          <span style={{ color: 'var(--red-700)' }}>Outstanding dues: <strong>{fmt(totals.outstandingDues)}</strong></span>
          <span style={{ color: 'var(--blue-700)' }}>Advance credits: <strong>{fmt(totals.advanceCredits)}</strong></span>
        </div>
      )}

      <div className="toolbar" style={{ marginTop: 8, justifyContent: 'center' }}>
        <span className="td-muted" style={{ fontSize: '0.8rem' }}>
          Showing {rows.length.toLocaleString()} of {total.toLocaleString()} invoice(s)
          {hasMore ? ' — scroll for more' : ''}
        </span>
      </div>

      {showAdd && (
        <AddBillingModal
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); reset(); }}
        />
      )}
      {showAddReceipt && (
        <AddNewReceiptModal
          onClose={() => setShowAddReceipt(false)}
          onCreated={() => { setShowAddReceipt(false); reset(); }}
        />
      )}
      {editTarget && (
        <EditBillModal
          invoice={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={(updated) => {
            // Patch the loaded row in place so scroll position is kept; totals re-fetch via tags.
            if (updated?.id) patchItem(updated.id, updated);
            setEditTarget(null);
          }}
        />
      )}
      {deepLinkInvoiceId && (
        <InvoiceDetailModal invoiceId={deepLinkInvoiceId} onClose={closeDeepLinkInvoice} />
      )}
    </>
  );
}

// Standalone invoice-detail overlay (line items + payment ledger + Clear Dues), reused both by
// this page's own ?openId= deep link AND by the app-wide detail panel (AppShell) so opening an
// invoice from anywhere else in the CRM (global search, etc.) never navigates away from wherever
// the user currently is — see features/ui/detailPanelSlice.js.
export function InvoiceDetailModal({ invoiceId, onClose }) {
  if (!invoiceId) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Invoice</h2>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body" style={{ padding: 0 }}>
          <InvoiceReceiptsDrawer invoiceId={invoiceId} />
        </div>
      </div>
    </div>
  );
}

// Receipt drawer — fetches full invoice (line items + payment ledger) for the row.
function InvoiceReceiptsDrawer({ invoiceId }) {
  const [showClearDues, setShowClearDues] = useState(false);
  const { data: inv, isFetching, error } = useGetInvoiceQuery(invoiceId);
  const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '—');

  if (isFetching && !inv) {
    return <div style={{ padding: 16 }}><div className="skeleton" style={{ height: 18, maxWidth: 280 }} /></div>;
  }
  if (error) {
    return <div className="td-muted" style={{ padding: 16 }}>Couldn't load receipts.</div>;
  }
  if (!inv) return null;

  const payments = inv.payments ?? [];
  const items = inv.items ?? [];
  const outstanding = Math.max(0, (inv.totalAmount ?? 0) - (inv.amountPaid ?? 0));

  // Payment method → badge colour.
  const methodBadgeClass = (m = '') => {
    const ml = m.toLowerCase();
    if (ml === 'upi' || ml === 'online') return 'b-blue';
    if (ml === 'card') return 'b-purple';
    if (ml === 'cash') return 'b-green';
    if (ml === 'banktransfer' || ml === 'bank transfer') return 'b-amber';
    return 'b-gray';
  };

  return (
    <div style={{ padding: '14px 18px', borderTop: '2px solid var(--border)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.5fr)', gap: 20 }}>

        {/* ── Line items ── */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', color: 'var(--text-muted)' }}>
              Itemized breakdown
            </span>
            {outstanding > 0 && (
              <button
                className="btn btn-outline btn-sm"
                style={{ fontSize: '0.74rem', padding: '3px 9px', gap: 5 }}
                onClick={() => setShowClearDues(true)}
              >
                <Receipt size={12} /> Clear Dues
              </button>
            )}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="inv-drawer-table">
              <colgroup>
                <col style={{ minWidth: 140 }} />
                <col style={{ width: 70 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 110 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Description</th>
                  <th style={{ textAlign: 'right' }}>Sessions</th>
                  <th style={{ textAlign: 'right' }}>Rate / session</th>
                  <th style={{ textAlign: 'right' }}>Sub-total</th>
                  <th style={{ textAlign: 'right' }}>Discount</th>
                  <th style={{ textAlign: 'right' }}>Net invoiced</th>
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? items.map((it) => (
                  <tr key={it.id}>
                    <td style={{ wordBreak: 'break-word' }}>{it.description}</td>
                    <td style={{ textAlign: 'right' }}>{it.quantity}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(it.unitPrice)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(it.quantity * it.unitPrice)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(inv.discountAmount)}</td>
                    <td style={{ textAlign: 'right' }} className="td-bold">{fmt(it.lineTotal)}</td>
                  </tr>
                )) : (
                  <tr>
                    <td style={{ wordBreak: 'break-word' }}>{inv.treatmentSummary || 'Billing entry'}</td>
                    <td style={{ textAlign: 'right' }}>{inv.totalSessions ?? 0}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(inv.costPerSession)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(inv.subTotal)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(inv.discountAmount)}</td>
                    <td style={{ textAlign: 'right' }} className="td-bold">{fmt(inv.totalAmount)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Outstanding summary pill */}
          {outstanding > 0 && (
            <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--red-50)', border: '1px solid var(--red-200, #fecaca)', borderRadius: 20, padding: '3px 10px', fontSize: '0.75rem' }}>
              <span style={{ color: 'var(--red-700)', fontWeight: 700 }}>Outstanding: {fmt(outstanding)}</span>
            </div>
          )}
        </div>

        {/* ── Receipts / payment ledger ── */}
        <div>
          <div style={{ textTransform: 'uppercase', fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 8 }}>
            Receipts <span style={{ fontWeight: 800, color: 'var(--text-secondary)' }}>({payments.length})</span>
          </div>
          {payments.length === 0 ? (
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: '10px 0' }}>
              No payments recorded yet against this invoice.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="inv-drawer-table">
                <colgroup>
                  <col style={{ width: 140 }} />
                  <col style={{ width: 170 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 100 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Receipt No.</th>
                    <th>Timestamp</th>
                    <th style={{ textAlign: 'center' }}>Method</th>
                    <th style={{ textAlign: 'right' }}>Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id}>
                      <td className="td-mono" style={{ fontWeight: 600 }}>{p.transactionReference || '—'}</td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{fmtDateTime(p.paidAt)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${methodBadgeClass(p.method)}`}>{p.method}</span>
                      </td>
                      <td style={{ textAlign: 'right' }} className="td-bold">{fmt(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showClearDues && (
        <AddNewReceiptModal
          initialInvoice={inv}
          onClose={() => setShowClearDues(false)}
          onCreated={() => setShowClearDues(false)}
        />
      )}
    </div>
  );
}

// "Clear Dues" — record a partial/full RECEIPT against a patient's outstanding balance.
// Never creates a new bill: it allocates the payment across existing unpaid invoices.
const SETTLE_METHODS = [
  { value: 'Cash', label: 'Cash' }, { value: 'Card', label: 'Card' }, { value: 'Upi', label: 'UPI' },
  { value: 'BankTransfer', label: 'Bank Transfer' }, { value: 'Online', label: 'Online' },
];
function ClearDuesModal({ onClose, onSettled }) {
  const [patient, setPatient] = useState(null);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('Cash');
  const [reference, setReference] = useState('');
  const { data: o } = useGetPatientOverviewQuery(patient?.id, { skip: !patient?.id });
  const [settle, { isLoading }] = useSettleDuesMutation();
  const outstanding = o?.outstanding ?? 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!patient?.id) { toast.error('Select a patient.'); return; }
    const amt = num(amount);
    if (amt <= 0) { toast.error('Enter a payment amount.'); return; }
    if (amt > outstanding + 0.01) { toast.error(`Amount exceeds outstanding dues of ${fmt(outstanding)}.`); return; }
    try {
      const res = await settle({
        patientId: patient.id, amount: amt, method,
        transactionReference: reference.trim() || null,
      }).unwrap();
      toast.success(`Receipt ${fmt(res.amountApplied)} recorded · ${fmt(res.remainingDues)} dues left`);
      onSettled();
    } catch (err) {
      toast.error(err?.data?.error?.message || 'Could not record the payment.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header"><h2>Clear dues (receipt)</h2><button type="button" className="modal-close" onClick={onClose}><X size={18} /></button></div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Patient *</label>
            <PatientCombobox value={patient} onChange={setPatient} placeholder="Search a patient by name, phone or ID…" />
          </div>
          {patient && (
            <div className="card card-body" style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between' }}>
              <span className="td-muted">Outstanding dues</span>
              <strong style={{ color: outstanding > 0 ? 'var(--red-700)' : 'var(--green-700)' }}>{fmt(outstanding)}</strong>
            </div>
          )}
          <div className="form-row">
            <div className="form-group"><label className="form-label">Payment amount (₹) *</label>
              <input className="form-control" type="number" min="0" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Method</label>
              <select className="form-control" value={method} onChange={(e) => setMethod(e.target.value)}>
                {SETTLE_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select></div>
          </div>
          <div className="form-group"><label className="form-label">Transaction reference</label>
            <input className="form-control" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" /></div>
          <div className="td-muted" style={{ fontSize: '0.76rem' }}>
            Logged as a receipt against existing invoices (oldest first). No new bill is created.
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={isLoading || !patient || outstanding <= 0}>
            {isLoading ? 'Recording…' : 'Record payment'}
          </button>
        </div>
      </form>
    </div>
  );
}

// "Add New Receipt" — find an existing bill by invoice number, then record a payment against it.
const ADD_RECEIPT_METHODS = [
  { value: 'Cash', label: 'Cash' }, { value: 'Card', label: 'Card' }, { value: 'Upi', label: 'UPI' },
  { value: 'BankTransfer', label: 'Bank Transfer' }, { value: 'Online', label: 'Online' },
];

/**
 * "Create a Receipt" — looked up by invoice number by default. When `initialPatientId` is
 * passed (the calendar's "Clear Dues" quick action), it skips straight to the patient's most
 * recent invoice instead of requiring a manual number lookup.
 */
export function AddNewReceiptModal({ onClose, onCreated, initialPatientId, initialPatientName, initialInvoice }) {
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [foundInvoice, setFoundInvoice] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('Cash');
  const [reference, setReference] = useState('');
  // Payment date — defaults to today (no extra step for the common case) but is editable so a
  // due cleared a few days ago can be backdated to when it actually happened, instead of every
  // receipt landing on "today" regardless of when the money actually came in.
  const [paidAtDate, setPaidAtDate] = useState(todayDateInput);
  const [triggerSearch] = useLazyListInvoicesQuery();
  const [addPayment, { isLoading }] = useAddPaymentMutation();

  // Pre-populate directly from a passed invoice (e.g. "Clear Dues" from drawer row).
  useEffect(() => {
    if (!initialInvoice) return;
    setFoundInvoice(initialInvoice);
    setInvoiceNumber(initialInvoice.invoiceNumber ?? '');
    const due = Math.max(0, (initialInvoice.totalAmount ?? 0) - (initialInvoice.amountPaid ?? 0));
    if (due > 0) setAmount(String(due));
  }, [initialInvoice]);

  const fetchDetails = async () => {
    const q = invoiceNumber.trim();
    if (!q) return;
    setFetching(true);
    setFetchError(null);
    setFoundInvoice(null);
    try {
      const result = await triggerSearch({ search: q, pageSize: 10 }).unwrap();
      const items = result?.items ?? [];
      // Prefer an exact case-insensitive match on invoice number; fall back to first result.
      const exact = items.find((i) => (i.invoiceNumber ?? '').toLowerCase() === q.toLowerCase());
      const match = exact || items[0];
      if (!match) { setFetchError('No invoice found with that number.'); return; }
      setFoundInvoice(match);
      setInvoiceNumber(match.invoiceNumber);
      const outstanding = Math.max(0, (match.totalAmount ?? 0) - (match.amountPaid ?? 0));
      if (outstanding > 0) setAmount(String(outstanding));
    } catch {
      setFetchError('Failed to fetch invoice details. Check the invoice number and try again.');
    } finally {
      setFetching(false);
    }
  };

  // Patient-bound mode: auto-resolve the patient's latest invoice instead of a manual lookup.
  useEffect(() => {
    if (!initialPatientId) return;
    let cancelled = false;
    (async () => {
      setFetching(true);
      setFetchError(null);
      try {
        const result = await triggerSearch({ patientId: initialPatientId, pageSize: 1, sortBy: 'issuedAt', sortDesc: true }).unwrap();
        if (cancelled) return;
        const match = result?.items?.[0];
        if (!match) { setFetchError(`No existing invoice found for ${initialPatientName || 'this patient'}.`); return; }
        setFoundInvoice(match);
        setInvoiceNumber(match.invoiceNumber);
        const outstanding = Math.max(0, (match.totalAmount ?? 0) - (match.amountPaid ?? 0));
        if (outstanding > 0) setAmount(String(outstanding));
      } catch {
        if (!cancelled) setFetchError("Could not load this patient's latest invoice.");
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPatientId]);

  const outstanding = foundInvoice
    ? Math.max(0, (foundInvoice.totalAmount ?? 0) - (foundInvoice.amountPaid ?? 0))
    : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!foundInvoice) { toast.error('Fetch invoice details first.'); return; }
    const amt = num(amount);
    if (amt <= 0) { toast.error('Enter a payment amount greater than zero.'); return; }
    if (!paidAtDate) { toast.error('Enter a payment date.'); return; }
    if (paidAtDate > todayDateInput()) { toast.error('Payment date cannot be in the future.'); return; }
    try {
      await addPayment({
        id: foundInvoice.id, amount: amt, method,
        transactionReference: reference.trim() || null,
        paidAt: dateInputToIso(paidAtDate),
      }).unwrap();
      toast.success(`Receipt of ${fmt(amt)} recorded on ${foundInvoice.invoiceNumber}`);
      onCreated();
    } catch (err) {
      toast.error(err?.data?.error?.message || 'Could not record receipt.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header">
          <h2>
            {initialInvoice
              ? `Clear Dues — ${initialInvoice.patientName || 'Patient'}`
              : initialPatientId
                ? `Create a Receipt — ${initialPatientName || 'Patient'}`
                : 'Add New Receipt'}
          </h2>
          <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          {initialInvoice ? (
            <div className="td-muted" style={{ fontSize: '0.8rem', marginBottom: 10 }}>
              Applying receipt to invoice <span className="td-mono td-bold">{initialInvoice.invoiceNumber}</span>. Fields are pre-filled — adjust as needed before recording.
            </div>
          ) : initialPatientId ? (
            <>
              {fetching && !foundInvoice && (
                <div className="td-muted" style={{ fontSize: '0.85rem', marginBottom: 10 }}>Loading latest invoice…</div>
              )}
              {fetchError && (
                <div style={{ color: 'var(--red-700)', fontSize: '0.85rem', marginBottom: 10 }}>{fetchError}</div>
              )}
              {foundInvoice && (
                <div className="td-muted" style={{ fontSize: '0.8rem', marginBottom: 10 }}>
                  Latest invoice: <span className="td-mono td-bold">{foundInvoice.invoiceNumber}</span> — this receipt will be recorded against it.
                </div>
              )}
            </>
          ) : (
            <div className="form-group">
              <label className="form-label">Invoice Number *</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="form-control" style={{ flex: 1 }}
                  placeholder="e.g. THC/26-27/439"
                  value={invoiceNumber}
                  onChange={(e) => { setInvoiceNumber(e.target.value); setFoundInvoice(null); setFetchError(null); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); fetchDetails(); } }}
                />
                <button
                  type="button" className="btn btn-outline"
                  onClick={fetchDetails}
                  disabled={fetching || !invoiceNumber.trim()}
                >
                  {fetching ? 'Fetching…' : 'Fetch All Details'}
                </button>
              </div>
              {fetchError && (
                <div style={{ color: 'var(--red-700)', fontSize: '0.8rem', marginTop: 4 }}>{fetchError}</div>
              )}
            </div>
          )}

          {foundInvoice && (
            <>
              <div className="card card-body" style={{ marginBottom: 12, fontSize: '0.85rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                <span className="td-muted">Patient</span>
                <span>{foundInvoice.patientName || '—'} {foundInvoice.patientCode ? <span className="td-mono">({foundInvoice.patientCode})</span> : ''}</span>
                <span className="td-muted">Treatment / Package</span>
                <span>{foundInvoice.treatmentSummary || '—'}</span>
                <span className="td-muted">Sessions</span>
                <span>{foundInvoice.totalSessions ?? 0}</span>
                <span className="td-muted">Rate / session</span>
                <span>{fmt(foundInvoice.costPerSession)}</span>
                <span className="td-muted">Total invoiced</span>
                <span className="td-bold">{fmt(foundInvoice.totalAmount)}</span>
                <span className="td-muted">Already paid</span>
                <span>{fmt(foundInvoice.amountPaid)}</span>
                <span className="td-muted">Outstanding balance</span>
                <span className="td-bold" style={{ color: outstanding > 0 ? 'var(--red-700)' : 'var(--green-700)' }}>
                  {fmt(outstanding)}
                </span>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Payment amount (₹) *</label>
                  <input
                    className="form-control" type="number" min="0.01" step="any" inputMode="decimal"
                    value={amount} onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Method</label>
                  <select className="form-control" value={method} onChange={(e) => setMethod(e.target.value)}>
                    {ADD_RECEIPT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Payment date *</label>
                  <input
                    className="form-control" type="date"
                    value={paidAtDate} max={todayDateInput()}
                    onChange={(e) => setPaidAtDate(e.target.value)}
                    title="When the payment actually happened — backdate if it was collected earlier and only now being recorded"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Transaction reference</label>
                <input
                  className="form-control"
                  value={reference} onChange={(e) => setReference(e.target.value)}
                  placeholder="Optional — a RCPT number is auto-assigned if left blank"
                />
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={isLoading || !foundInvoice || !amount}>
            {isLoading ? 'Recording…' : 'Record Receipt'}
          </button>
        </div>
      </form>
    </div>
  );
}

// Manual billing-register entry. The server generates the next THC/YY-YY/N number.
const MANUAL_MARKETING_SOURCES = [
  { value: 'Google', label: 'Google' },
  { value: 'Facebook', label: 'Facebook' },
  { value: 'Instagram', label: 'Instagram' },
  { value: 'WalkIn', label: 'Walk-In' },
  { value: 'Reference', label: 'Reference' },
];

const newLine = () => ({ id: crypto.randomUUID(), description: '', costPerSession: '', sessions: '1' });

// "Today" as the operator sees it on their own clock — new Date().toISOString().slice(0,10)
// is the UTC day, which is still *yesterday* between 00:00 and 05:30 IST.
const todayDateInput = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// Date-only input → ISO instant at local NOON, so the stored IssuedAt lands inside the intended
// IST calendar day (and FY) no matter which side of a UTC day boundary the clinic clock is on.
// new Date('YYYY-MM-DD') would be UTC midnight, i.e. 18:30 the previous IST day's FY at Apr 1.
const dateInputToIso = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0).toISOString();
};

function AddBillingModal({ onClose, onCreated }) {
  const [patient, setPatient] = useState(null);
  const [showQuickAddPatient, setShowQuickAddPatient] = useState(false);
  const [lines, setLines] = useState([newLine()]);
  const [discountAmount, setDiscountAmount] = useState('0');
  const [amountPaid, setAmountPaid] = useState('0');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [issuedAt, setIssuedAt] = useState(todayDateInput);
  const [marketingSource, setMarketingSource] = useState('');
  const [referralName, setReferralName] = useState('');
  const { data: ov } = useGetPatientOverviewQuery(patient?.id, { skip: !patient?.id });
  const [createBilling, { isLoading }] = useCreateManualBillingMutation();
  const { data: catalog = [] } = useListProcedureCatalogQuery(true);
  const { data: nextNumber } = usePeekNextInvoiceNumberQuery(issuedAt ? dateInputToIso(issuedAt) : undefined);

  // Invoice number: pre-filled with the server's preview but left fully editable so an
  // operator can match an existing physical book series. We re-check for a clash on every
  // edit (debounced) — never on the master catalog, just this one number.
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [numberTouched, setNumberTouched] = useState(false);
  useEffect(() => {
    if (!numberTouched && nextNumber) setInvoiceNumber(nextNumber);
  }, [nextNumber, numberTouched]);
  const [triggerFindByNumber] = useLazyFindInvoiceByNumberQuery();
  const [numberClash, setNumberClash] = useState(null);
  useEffect(() => {
    const n = invoiceNumber.trim();
    if (!n) { setNumberClash(null); return; }
    const t = setTimeout(async () => {
      try {
        const match = await triggerFindByNumber(n).unwrap();
        setNumberClash(match || null);
      } catch { setNumberClash(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [invoiceNumber, triggerFindByNumber]);

  // Selecting a catalog procedure seeds description + rate for that line only — the rate
  // stays a normal editable input, and nothing here ever writes back to the master catalog.
  const applyCatalogItem = (id, procedureId) => {
    const p = catalog.find((c) => c.id === procedureId);
    if (!p) return;
    setLines((prev) => prev.map((l) => l.id === id ? { ...l, description: p.name, costPerSession: String(p.baseCost) } : l));
  };

  const updateLine = (id, field, value) =>
    setLines((prev) => prev.map((l) => l.id === id ? { ...l, [field]: value } : l));
  const addLine = () => setLines((prev) => [...prev, newLine()]);
  const removeLine = (id) => setLines((prev) => prev.length > 1 ? prev.filter((l) => l.id !== id) : prev);

  // First invoice → marketing attribution is mandatory.
  const isFirstInvoice = ov != null && (ov.invoices?.length ?? 0) === 0;
  const attributionError = isFirstInvoice
    ? (!marketingSource
        ? 'Select a marketing source.'
        : (marketingSource === 'Reference' && !referralName.trim() ? 'Enter the referral name.' : null))
    : null;

  const parsedLines = lines.map((l) => ({
    ...l,
    cps: Math.max(0, num(l.costPerSession)),
    sess: Math.max(0, Math.round(num(l.sessions))),
  }));
  const subTotal = parsedLines.reduce((s, l) => s + l.cps * l.sess, 0);
  const disc = Math.min(num(discountAmount), subTotal);
  const invoiced = Math.max(0, subTotal - disc);
  const balance = invoiced - num(amountPaid);

  const submit = async (e) => {
    e.preventDefault();
    if (!patient?.id) { toast.error('Select a patient.'); return; }
    // Sessions are required per line; the rate itself may legitimately be 0 (e.g. a free visit).
    const validLines = parsedLines.filter((l) => l.sess > 0);
    if (validLines.length === 0) { toast.error('Add at least one line item with sessions.'); return; }
    if (attributionError) { toast.error(attributionError); return; }
    if (numberClash) { toast.error(`Invoice number ${invoiceNumber.trim()} is already used.`); return; }
    try {
      const inv = await createBilling({
        patientId: patient.id,
        invoiceNumber: invoiceNumber.trim() || null,
        items: validLines.map((l) => ({
          description: l.description.trim() || 'Service',
          unitPrice: l.cps,
          quantity: l.sess,
        })),
        discountAmount: num(discountAmount),
        amountPaid: num(amountPaid),
        method: paymentMethod,
        issuedAt: dateInputToIso(issuedAt),
        marketingSource: isFirstInvoice && marketingSource ? marketingSource : null,
        referralName: isFirstInvoice && marketingSource === 'Reference' ? referralName.trim() : null,
      }).unwrap();
      toast.success(`Billing ${inv?.invoiceNumber || ''} created`);
      onCreated();
    } catch (err) {
      toast.error(err?.data?.error?.message || 'Could not create billing entry.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" style={{ maxWidth: 680 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header">
          <h2>Add billing entry</h2>
          <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Invoice number</label>
            <input
              className="form-control td-mono"
              style={numberClash ? { borderColor: 'var(--red-500)' } : undefined}
              value={invoiceNumber}
              placeholder={nextNumber || 'Auto-generated'}
              onChange={(e) => { setInvoiceNumber(e.target.value); setNumberTouched(true); }}
            />
            {numberClash ? (
              <div style={{ color: 'var(--red-700)', fontSize: '0.78rem', marginTop: 4 }}>
                Already used — {numberClash.patientName || 'a patient'} on {fmtDate(numberClash.issuedAt)}.
              </div>
            ) : (
              <div className="td-muted" style={{ fontSize: '0.76rem', marginTop: 4 }}>
                Pre-filled with the next auto-generated number — edit only to match an existing series.
              </div>
            )}
          </div>
          {/* Patient */}
          <div className="form-group">
            <label className="form-label">Patient *</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <PatientCombobox value={patient} onChange={setPatient} placeholder="Search a patient by name, phone or ID…" />
              </div>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                title="Register a brand-new patient without leaving this form"
                onClick={() => setShowQuickAddPatient(true)}
              >
                <UserPlus size={14} /> New Patient
              </button>
            </div>
          </div>

          {/* Line items */}
          <div className="form-group">
            <label className="form-label">Procedures / treatments *</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Header row */}
              <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr 110px 72px 32px', gap: 6, paddingRight: 0 }}>
                <span className="td-muted" style={{ fontSize: '0.74rem', paddingLeft: 2 }}>Catalog</span>
                <span className="td-muted" style={{ fontSize: '0.74rem' }}>Description</span>
                <span className="td-muted" style={{ fontSize: '0.74rem' }}>Rate (₹/session)</span>
                <span className="td-muted" style={{ fontSize: '0.74rem' }}>Sessions</span>
                <span />
              </div>
              {lines.map((l) => (
                <div key={l.id} style={{ display: 'grid', gridTemplateColumns: '150px 1fr 110px 72px 32px', gap: 6, alignItems: 'center' }}>
                  <select
                    className="form-control"
                    value=""
                    title="Pick a procedure to auto-fill description and rate (still editable)"
                    onChange={(e) => { if (e.target.value) applyCatalogItem(l.id, e.target.value); e.target.value = ''; }}
                  >
                    <option value="">— Catalog —</option>
                    {catalog.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <input
                    className="form-control"
                    placeholder="e.g. Physiotherapy"
                    value={l.description}
                    onChange={(e) => updateLine(l.id, 'description', e.target.value)}
                  />
                  <input
                    className="form-control"
                    type="number" min="0" inputMode="decimal"
                    placeholder="0"
                    value={l.costPerSession}
                    onChange={(e) => updateLine(l.id, 'costPerSession', e.target.value)}
                  />
                  <input
                    className="form-control"
                    type="number" min="1" step="1"
                    placeholder="1"
                    value={l.sessions}
                    onChange={(e) => updateLine(l.id, 'sessions', e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn-icon-sm"
                    title="Remove line"
                    disabled={lines.length === 1}
                    onClick={() => removeLine(l.id)}
                    style={{ opacity: lines.length === 1 ? 0.3 : 1 }}
                  >
                    <Trash2 size={14} color="var(--red-500)" />
                  </button>
                </div>
              ))}
              <button type="button" className="btn btn-sm btn-outline" style={{ alignSelf: 'flex-start' }} onClick={addLine}>
                <Plus size={13} /> Add line
              </button>
            </div>
          </div>

          {/* Discount / paid / method / date */}
          <div className="form-row">
            <div className="form-group"><label className="form-label">Discount (₹)</label>
              <input className="form-control" type="number" min="0" inputMode="decimal" value={discountAmount} onChange={(e) => setDiscountAmount(e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Amount paid (₹)</label>
              <input className="form-control" type="number" min="0" inputMode="decimal" value={amountPaid} onChange={(e) => setAmountPaid(e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Payment method</label>
              <select className="form-control" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select></div>
          </div>
          <div className="form-group"><label className="form-label">Issue date</label>
            <input className="form-control" type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} /></div>

          {/* First-billing attribution */}
          {isFirstInvoice && (
            <div className="card card-body" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', marginBottom: 10 }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--blue-800)', marginBottom: 8 }}>
                First billing — how did this patient find us? *
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Marketing source *</label>
                  <select className="form-control" value={marketingSource} onChange={(e) => setMarketingSource(e.target.value)}>
                    <option value="">— Select —</option>
                    {MANUAL_MARKETING_SOURCES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select></div>
                {marketingSource === 'Reference' && (
                  <div className="form-group"><label className="form-label">Referral name *</label>
                    <input className="form-control" value={referralName} onChange={(e) => setReferralName(e.target.value)} placeholder="Who referred them?" /></div>
                )}
              </div>
              <div className="td-muted" style={{ fontSize: '0.74rem' }}>The initial consultation doctor is linked automatically.</div>
            </div>
          )}

          {/* Totals summary */}
          <div className="card card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: '0.85rem' }}>
            <span className="td-muted">Subtotal</span><span style={{ textAlign: 'right' }}>{fmt(subTotal)}</span>
            <span className="td-muted">Invoiced (after disc.)</span><span style={{ textAlign: 'right' }}>{fmt(invoiced)}</span>
            <span className="td-muted">Balance</span>
            <span style={{ textAlign: 'right', fontWeight: 700, color: balance > 0 ? 'var(--red-700)' : balance < 0 ? 'var(--blue-700)' : 'var(--green-700)' }}>
              {balance < 0 ? `+${fmt(-balance)}` : fmt(balance)}
            </span>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={isLoading || !!numberClash}>{isLoading ? 'Saving…' : 'Create billing'}</button>
        </div>
      </form>

      {showQuickAddPatient && (
        <QuickAddPatientModal
          onClose={() => setShowQuickAddPatient(false)}
          onCreated={(p) => { setPatient(p); setShowQuickAddPatient(false); }}
        />
      )}
    </div>
  );
}

// ── Quick "register new patient" modal — launched inline from AddBillingModal ──
function QuickAddPatientModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [gender, setGender] = useState('');
  const [dob, setDob] = useState('');
  const [createPatient, { isLoading }] = useCreatePatientMutation();

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Full name is required.'); return; }
    // The backend requires a phone number for every patient — validate here too, or this fails
    // with a server-side "phone required" error after the user has already hit submit.
    if (!phone.trim()) { toast.error('Phone number is required.'); return; }
    try {
      const p = await createPatient({
        fullName: name.trim(),
        // Must match CreatePatientRequest.PhoneNumber exactly — "phone" doesn't bind to it at
        // all, so the field silently arrived empty and every submission failed validation.
        phoneNumber: phone.trim(),
        gender: gender || null,
        dateOfBirth: dob || null,
      }).unwrap();
      toast.success(`Patient "${p.fullName || name.trim()}" registered and selected.`);
      onCreated({ id: p.id, name: p.fullName || p.name || name.trim() });
    } catch (err) {
      toast.error(err?.data?.error?.message || 'Could not register patient. Try again.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1100 }}>
      <form className="modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserPlus size={18} /> Register new patient
          </h2>
          <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="td-muted" style={{ fontSize: '0.78rem', marginBottom: 14, background: 'var(--blue-50)', border: '1px solid var(--blue-200, #bfdbfe)', borderRadius: 8, padding: '8px 12px' }}>
            The new patient will be registered and automatically selected in the billing form.
          </div>
          <div className="form-group">
            <label className="form-label">Full name *</label>
            <input className="form-control" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ravi Sharma" />
          </div>
          <div className="form-group">
            <label className="form-label">Phone *</label>
            <input className="form-control" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile" />
          </div>
          {/* No email field here — patient records in this system don't have one; only phone-based
              channels (SMS/WhatsApp) are used for outreach. */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Gender</label>
              <select className="form-control" value={gender} onChange={(e) => setGender(e.target.value)}>
                <option value="">— Select —</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Date of birth</label>
              <input className="form-control" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? 'Registering…' : <><UserPlus size={14} /> Register &amp; select</>}
          </button>
        </div>
      </form>
    </div>
  );
}

function DuesTab() {
  // Filter outstanding dues by invoice ISSUE date (DueAt is rarely populated — bills here are
  // issued and payable the same day, so issue date is the meaningful axis).
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const { data: dues, isFetching } = useListDuesQuery({ from: from || undefined, to: to || undefined });
  const total = (dues ?? []).reduce((s, i) => s + Math.max(0, (i.totalAmount ?? 0) - (i.amountPaid ?? 0)), 0);
  return (
    <>
      <div className="toolbar" style={{ gap: 8, alignItems: 'center' }}>
        <span className="td-muted" style={{ fontSize: '0.84rem' }}>Issued from</span>
        <input className="form-control" type="date" style={{ width: 150 }} value={from}
          onChange={(e) => setFrom(e.target.value)} title="Invoice issue date — start" />
        <span className="td-muted" style={{ fontSize: '0.84rem' }}>to</span>
        <input className="form-control" type="date" style={{ width: 150 }} value={to}
          onChange={(e) => setTo(e.target.value)} title="Invoice issue date — end (inclusive)" />
        {(from || to) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setFrom(''); setTo(''); }}>Clear</button>
        )}
        <span className="td-muted" style={{ marginLeft: 'auto', fontSize: '0.84rem' }}>
          {(dues ?? []).length} invoice(s) · outstanding <strong style={{ color: 'var(--red-700)' }}>{fmt(total)}</strong>
        </span>
      </div>
      <InvoiceTable rows={dues ?? []} loading={isFetching} />
    </>
  );
}

function InvoiceTable({ rows, loading }) {
  return (
    <div className="tbl-wrap">
      <table className="invoice-table">
        <thead>
          <tr>
            <th>Invoice #</th><th>Patient</th><th>Issued</th><th>Due</th><th>Total</th><th>Paid</th><th>Balance</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24 }}>
              <div className="skeleton" style={{ height: 20, maxWidth: 280, margin: '0 auto' }} />
            </td></tr>
          )}
          {rows.map((i) => {
            return (
              <tr key={i.id}>
                <td className="td-mono td-bold">{i.invoiceNumber}</td>
                <td>{i.patientName ?? i.patientId?.slice(0, 8)}</td>
                <td className="td-muted">{fmtDate(i.issuedAt)}</td>
                <td className="td-muted">{fmtDate(i.dueAt)}</td>
                <td>{fmt(i.totalAmount)}</td>
                <td>{fmt(i.amountPaid)}</td>
                <td className="td-bold" style={{ color: (i.totalAmount - i.amountPaid) > 0 ? 'var(--red-700)' : 'var(--green-700)' }}>
                  {fmt(i.totalAmount - i.amountPaid)}
                </td>
              </tr>
            );
          })}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={7}>
              <div className="empty-state">
                <IndianRupee className="es-icon" size={42} />
                <h3>No invoices</h3>
                <p>Create an invoice to start tracking billing.</p>
              </div>
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
